import type { ToolPolicyRule } from '@af/shared';
import { DEFAULT_EFFECT_DECISIONS } from '@af/shared';
import { useTranslation } from 'react-i18next';
import { Badge, Section } from '@/components/ui/misc';
import { Switch } from '@/components/ui/switch';
import { BUILTIN_TOOLS, EFFECT_TONE, PERMISSIONS } from '@/lib/catalog';
import { globMatch, PolicyEditor } from './PolicyEditor';

export function effectiveDecision(tool: { name: string; effect: string }, rules: ToolPolicyRule[]) {
  const matching = rules
    .filter((r) => globMatch(r.toolPattern, tool.name) && (!r.effect || r.effect === tool.effect))
    .sort(
      (a, b) =>
        Number(b.toolPattern === tool.name) - Number(a.toolPattern === tool.name) || b.priority - a.priority,
    );
  return (
    matching[0]?.decision ??
    DEFAULT_EFFECT_DECISIONS[tool.effect as keyof typeof DEFAULT_EFFECT_DECISIONS] ??
    'ask'
  );
}

export function ToolsFields({
  builtinTools,
  toolPolicy,
  onChange,
  disabled,
}: {
  builtinTools: string[];
  toolPolicy: ToolPolicyRule[];
  onChange: (v: { builtinTools: string[]; toolPolicy: ToolPolicyRule[] }) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const toggle = (name: string, on: boolean) =>
    onChange({
      toolPolicy,
      builtinTools: on ? [...new Set([...builtinTools, name])] : builtinTools.filter((x) => x !== name),
    });
  return (
    <div className="flex flex-col gap-3">
      <Section title="Built-in tools">
        <ul className="grid grid-cols-1 gap-x-6 gap-y-1 lg:grid-cols-2">
          {BUILTIN_TOOLS.map((tool) => {
            const on = builtinTools.includes(tool.name);
            const decision = effectiveDecision(tool, toolPolicy);
            return (
              <li key={tool.name} className="flex min-h-8 items-center gap-2">
                <Switch
                  id={`tool-${tool.name}`}
                  checked={on}
                  disabled={disabled}
                  onCheckedChange={(v) => toggle(tool.name, v)}
                  aria-label={`Enable ${tool.name}`}
                />
                <label htmlFor={`tool-${tool.name}`} className="min-w-0 flex-1 cursor-pointer">
                  <span className="font-bold">{tool.name}</span>
                  {tool.phase ? <span className="ml-1 text-2xs text-muted">(phase {tool.phase})</span> : null}
                  <span className="block truncate text-2xs text-muted">{tool.description}</span>
                </label>
                <Badge tone={EFFECT_TONE[tool.effect]}>{tool.effect}</Badge>
                <Badge
                  tone={decision === 'allow' ? 'success' : decision === 'ask' ? 'warn' : 'danger'}
                  title="Effective decision"
                >
                  {on ? decision : 'off'}
                </Badge>
              </li>
            );
          })}
        </ul>
      </Section>
      <Section title={t('company.permissions')}>
        <ul className="flex flex-col gap-1">
          {PERMISSIONS.map((p) => (
            <li key={p.name} className="flex min-h-8 items-start gap-2 py-1">
              <Switch
                id={`perm-${p.name}`}
                checked={builtinTools.includes(p.name)}
                disabled={disabled}
                onCheckedChange={(v) => toggle(p.name, v)}
                aria-label={t(p.labelKey)}
              />
              <label htmlFor={`perm-${p.name}`} className="min-w-0 flex-1 cursor-pointer">
                <span className="font-bold">{t(p.labelKey)}</span>
                <span className="ml-1 font-mono text-2xs text-muted">{p.name}</span>
                <span className="block text-2xs text-muted">{t(p.hintKey)}</span>
                <span className="block font-mono text-2xs text-muted">
                  {t('company.unlocks', { tools: p.unlocks.join(', ') })}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </Section>
      <Section title="Policy rules">
        <PolicyEditor
          rules={toolPolicy}
          disabled={disabled}
          onChange={(r) => onChange({ builtinTools, toolPolicy: r })}
        />
      </Section>
    </div>
  );
}
